import { useState } from "react";
import AnimateOnScroll from "./AnimateOnScroll";
import { useNavigate } from "react-router-dom";

const REGISTER_URL =
  "https://id.production.envizage.me/realms/c71accc6-d6a8-400b-8956-142a1fb9c57b/protocol/openid-connect/auth?client_id=b414c6ec-7a9e-4d43-af8e-c863b2623f56&redirect_uri=https%3A%2F%2Fcfp.envizage.ai%2F&state=37b650fb-2cc2-40a3-b5f0-95ad139fc3cf&response_mode=fragment&response_type=code&scope=openid&nonce=cc28099f-402a-410b-ae1f-eb76b93f3d3d&code_challenge=KSI36IXuIVPHe71mh52L1RbODywaZfDuH-gVpctXezM&code_challenge_method=S256";

const plans = [
  {
    tier: "Co-Creator Monthly",
    price: "£3.25",
    priceSuffix: "/ month per end user",
    badge: null, //  "Limited Spots",
    badgeBg: "bg-amber-100 text-amber-700 border border-amber-300",
    accent: "bg-amber-400",
    highlight: "75% off — through 2026",
    highlightIcon: "ri-vip-crown-line",
    highlightColor: "text-amber-600 bg-amber-50 border border-amber-200",
    desc: "Shape the product directly with the team. Offer closes 30 Sep 2026.",
    // note: "Per end user, per month.",
    features: [
      { text: "Full platform access", included: true },
      { text: "CFP-grade financial forecasts", included: true },
      { text: "Unlimited client planning sessions", included: true },
      {
        text: "Branded white-label interface & printable report",
        included: true,
      },
      { text: "Compliance audit trail", included: true },
      { text: "UK-hosted, GDPR-compliant storage", included: true },
      { text: "Direct input into the product roadmap", included: true },
      { text: "Lowest rate locked in thru 2026", included: true },
    ],
    cta: "Buy Now",
    ctaUrl: REGISTER_URL,
    ctaEnabled: true,
    ctaStyle: "bg-[#222222] hover:bg-[#000000] text-[#ffffff]",
  },
  {
    tier: "Monthly Subscription",
    price: "£12.99",
    priceSuffix: "/ month per end user",
    badge: "Most Popular",
    badgeBg: "bg-brown-100 text-white",
    accent: "bg-[#1BB184]",
    highlight: "Best choice",
    highlightIcon: "ri-calendar-check-line",
    highlightColor: "text-[#1BB184] bg-[#E8F5EE] border border-[#B8DFD0]",
    desc: "Full platform access on a rolling monthly basis. Minimum 3-month commitment.",
    // note: "Per end user, per month.",
    subnote: "End user or adviser pays options.",
    features: [
      { text: "Full platform access", included: true },
      { text: "CFP-grade financial forecasts", included: true },
      { text: "Unlimited client planning sessions", included: true },
      {
        text: "Planner pays for end users — or end users self-subscribe",
        included: true,
      },
      {
        text: "Branded white-label interface & printable report",
        included: true,
      },
      { text: "Compliance audit trail", included: true },
      { text: "UK-hosted, GDPR-compliant storage", included: true },
      { text: "Email support for financial planners", included: true },
    ],
    cta: "Buy Now",
    ctaUrl: REGISTER_URL,
    ctaEnabled: true,
    ctaStyle: "bg-[#222222] hover:bg-[#000000] text-[#ffffff]",
  },
  {
    tier: "Single Use",
    price: "£49.99",
    priceSuffix: "one-off",
    badge: null,
    badgeBg: "",
    accent: "bg-[#222]",
    highlight: "Valid for 24 hours",
    highlightIcon: "ri-time-line",
    highlightColor: "text-[#1a1a2e] bg-[#F0EBE0] border border-[#D8D0C4]",
    desc: "Instant access for 24 hours — ideal for prospecting meetings and one-off client conversations.",
    note: null,
    features: [
      { text: "24-hour full platform access", included: true },
      { text: "CFP-grade financial forecasts", included: true },
      { text: "One-off client planning sessions", included: true },
      { text: "Printable planning summary", included: true },
      { text: "No subscription required", included: true },
      { text: "Branded white-label interface", included: false },
      { text: "Ongoing client portal access", included: false },
      { text: "Compliance audit trail", included: false },
    ],
    cta: "Buy Now",
    ctaUrl: "/single-use",
    ctaEnabled: true,
    ctaStyle: "bg-[#222] hover:bg-[#000] text-white",
  },
];

const pricingFaqs = [
  {
    q: "Is there a long-term contract?",
    a: "The Monthly Subscription has a 3-month minimum commitment. After that it rolls monthly — no annual lock-in, no cancellation fees. When you're ready to cancel, just do it yourself in the account settings; your subscription ends immediately and access continues until the end of your current billing cycle.",
  },
  {
    q: "How does cancellation actually work?",
    a: "You can export all data entered via the report facility.",
  },
  {
    q: "What happens to my client data if I leave?",
    a: "You can export all client data via the report facility. We retain nothing after 30 days of account closure, in line with our GDPR obligations.",
  },
  {
    q: "Can the price change during my subscription?",
    a: "No. Your rate is locked for the duration of any active subscription term.",
  },
  {
    q: "Does the Single Use option need a card on file?",
    a: "No recurring charge is set up — it's a one-off transaction. You'll get 24 hours of full access from the moment of purchase, no auto-renewal.",
  },
  {
    q: "Is VAT included in the prices shown?",
    a: "All prices shown are inclusive of VAT.",
  },
  {
    q: 'What counts as an "end user" and can one planner manage multiple?',
    a: "An end user is any individual — typically a client — who accesses the platform to run financial planning sessions. A single Financial Planner account can manage multiple end user subscriptions from one place. If the planner is paying on behalf of their clients, each active end user adds £12.99/month to the total. If end users self-subscribe, each manages and pays for their own access independently, with no impact on the planner's billing.",
  },
];

export default function PricingSection() {
  const navigate = useNavigate();
  const openCtaUrl = (url: string) => {
    if (url.startsWith("/")) {
      navigate(url);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const [faqOpen, setFaqOpen] = useState(false);

  return (
    <section id="pricing" className="bg-[#EFEBE5] py-24">
      <div className="max-w-6xl mx-auto px-6">
        <AnimateOnScroll variant="fade-up">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-widest text-brown-300 uppercase mb-4">
              Pricing
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1a1a2e] mb-5">
              Flexible Access for Every Planner
            </h2>
            <p className="text-base text-[#6a6a7a] max-w-xl mx-auto leading-relaxed">
              From a single prospecting session to a full monthly subscription —
              pick the access model that fits how you work.
            </p>
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {plans.map((plan, i) => (
            <AnimateOnScroll key={plan.tier} variant="fade-up" delay={i * 120}>
              <div className="relative  rounded-2xl bg-brown-300 h-full  transition-all duration-300">
                {/* Accent top strip */}
                {/* <div className={`h-1 w-full ${plan.accent}`} /> */}
                {/* bg-white rounded-2xl  */}
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute top-6 right-5">
                    <span
                      className={`text-xs font-bold px-3 py-1 rounded-2xl whitespace-nowrap ${plan.badgeBg}`}
                    >
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* Header */}
                <div className="flex flex-1 flex-col justify-between p-6 sm:p-8 pb-5 border-b border-[#EDE8DF] rounded-2xl border border-transparent hover:border-[#C0B8B0] bg-brown-10 shadow-lg md:min-h-[380px]">
                  <div>
                    <p className="text-xs font-bold tracking-widest uppercase text-[#9a9aaa] mb-4 pr-24 leading-tight">
                      {plan.tier}
                    </p>
                    <div className="flex items-end gap-1.5 mb-3">
                      <span className="text-4xl font-regular text-[#1a1a2e] leading-none">
                        {plan.price}
                      </span>
                      <span className="text-sm text-[#9a9aaa] mb-1">
                        {plan.priceSuffix}
                      </span>
                    </div>

                    <div
                      className={`inline-flex items-center gap-1.5 mb-4 px-3 py-1.5 rounded-2xl ${plan.highlightColor}`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${plan.highlightIcon} text-xs`}></i>
                      </div>
                      <span className="text-xs font-semibold whitespace-nowrap">
                        {plan.highlight}
                      </span>
                    </div>

                    <p className="text-md text-[#6a6a7a] leading-relaxed">
                      {plan.desc}
                    </p>
                    {plan.note && (
                      <p className="text-md mt-3 font-semibold text-[#1a1a2e]">
                        {plan.note}
                      </p>
                    )}
                    {plan.subnote && (
                      <span className="text-sm mt-3 text-[#7a7a8a]">
                        {plan.subnote}
                      </span>
                    )}
                  </div>

                  {/* CTA */}
                  <div className="pt-6 sm:pt-4">
                    <button
                      disabled={!plan.ctaEnabled}
                      onClick={() => openCtaUrl(plan.ctaUrl)}
                      className={`w-full py-3 rounded-full text-md font-semibold transition-colors whitespace-nowrap ${plan.ctaEnabled ? "cursor-pointer" : "opacity-50 cursor-not-allowed"} ${plan.ctaStyle}`}
                    >
                      {plan.cta}
                    </button>
                  </div>
                </div>

                {/* Features */}
                <div className="p-6 sm:p-8 flex flex-col gap-3 flex-1">
                  {plan.features.map((f) => (
                    <div key={f.text} className="flex items-start gap-3">
                      <div
                        className={`w-5 h-5 flex items-center justify-center rounded flex-shrink-0 mt-0.5 ${f.included ? "bg-brown-300" : "bg-brown-300"}`}
                      >
                        <i
                          className={`text-md ${f.included ? "ri-check-line text-[#222222]" : "ri-close-line text-[#C0B8B0]"}`}
                        ></i>
                      </div>
                      <span
                        className={`text-md leading-relaxed ${f.included ? "text-[#3a3a4a]" : "text-[#C0B8B0]"}`}
                      >
                        {f.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimateOnScroll>
          ))}
        </div>

        {/* Two-path explainer */}
        <AnimateOnScroll variant="fade-up" delay={80}>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-4 rounded-2xl border border-[#EDE8DF] bg-white p-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-brown-300 flex-shrink-0">
                <i className="ri-bank-card-line text-lg text-brown-300"></i>
              </div>
              <div>
                <p className="text-md font-bold text-[#1a1a2e] mb-1">
                  Planner pays on behalf of end users
                </p>
                <p className="text-md text-[#6a6a7a] leading-relaxed">
                  The Financial Planner subscribes and covers the monthly cost
                  per end user. End users access the platform directly — no
                  billing set-up required on their side.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-2xl border border-[#EDE8DF] bg-white p-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-brown-300 flex-shrink-0">
                <i className="ri-mail-send-line text-lg text-brown-300"></i>
              </div>
              <div>
                <p className="text-md font-bold text-[#1a1a2e] mb-1">
                  Planner invites end users to self-subscribe
                </p>
                <p className="text-md text-[#6a6a7a] leading-relaxed">
                  The Financial Planner sends an invite link. Each end user
                  subscribes and manages their own monthly subscription
                  independently.
                </p>
              </div>
            </div>
          </div>
        </AnimateOnScroll>

        {/* Pricing FAQs */}
        <AnimateOnScroll variant="fade-up" delay={100}>
          <div className="mt-8 rounded-2xl bg-white border border-[#EDE8DF] overflow-hidden">
            <button
              onClick={() => setFaqOpen((v) => !v)}
              className="w-full px-5 sm:px-8 py-5 flex items-center gap-3 flex-wrap cursor-pointer hover:bg-[#faf7f2] transition-colors"
            >
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-brown-300">
                <i className="ri-question-answer-line text-sm text-gree-300"></i>
              </div>
              <h3 className="text-md font-bold text-[#1a1a2e] tracking-tight">
                Before you commit — quick answers
              </h3>
              <span className="ml-auto flex items-center gap-3">
                <span className="text-xs text-[#9a9aaa] whitespace-nowrap">
                  {pricingFaqs.length} common questions
                </span>
                <div className="w-6 h-6 flex items-center justify-center rounded bg-[#F0EBE0]">
                  <i
                    className={`text-md text-[#1a1a2e] transition-transform duration-300 ${faqOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}
                  ></i>
                </div>
              </span>
            </button>

            {faqOpen && (
              <div className="border-t border-[#EDE8DF]">
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#EDE8DF]">
                  <div className="divide-y divide-[#EDE8DF]">
                    {pricingFaqs
                      .filter((_, i) => i % 2 === 0)
                      .map((faq) => (
                        <div key={faq.q} className="px-5 sm:px-8 py-5 sm:py-6">
                          <p className="text-md font-semibold text-[#1a1a2e] mb-2 flex items-start gap-2">
                            <span className="mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0">
                              <i className="ri-checkbox-circle-line text-[#038C7F] text-base"></i>
                            </span>
                            {faq.q}
                          </p>
                          <p className="text-md text-[#6a6a7a] leading-relaxed pl-6">
                            {faq.a}
                          </p>
                        </div>
                      ))}
                  </div>
                  <div className="divide-y divide-[#EDE8DF]">
                    {pricingFaqs
                      .filter((_, i) => i % 2 !== 0)
                      .map((faq) => (
                        <div key={faq.q} className="px-5 sm:px-8 py-5 sm:py-6">
                          <p className="text-md font-semibold text-[#1a1a2e] mb-2 flex items-start gap-2">
                            <span className="mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0">
                              <i className="ri-checkbox-circle-line text-[#038C7F] text-base"></i>
                            </span>
                            {faq.q}
                          </p>
                          <p className="text-md text-[#6a6a7a] leading-relaxed pl-6">
                            {faq.a}
                          </p>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
}
